import { TextArea } from '@ksyos/design-system';
import classnames from 'classnames';
import { type CSSProperties, useEffect, useState } from 'react';
import { getValidationErrorTranslation } from '@frontend/utils';
import type { IFieldProps } from '../elements/Field';
import type { FormContextProps } from '../elements/formContext';
import { Field } from '../elements/Field';
import { FormContextConsumer } from '../elements/formContext';

export interface ITextAreaInputProps {
    automation?: string;
    autoResize?: boolean;
    disabled?: boolean;
    error?: string;
    maxLength?: number;
    name?: string;
    onChange: (value: string | null) => void;
    onDoValidate?: () => void;
    placeholder?: string;
    reserveErrorSpace?: boolean;
    rows?: number;
    style?: CSSProperties;
    tabIndex?: number;
    value: string | null;
    variant?: 'outline' | 'flushed';
}

/**
 * @deprecated Use TextAreaField
 */
export function TextAreaInput(props: FormContextProps<ITextAreaInputProps>) {
    const [key, setKey] = useState('');

    // Workaround for autoResize issue in class components:
    // Assigning a unique key to the TextArea component forces React to remount the component whenever the key changes.
    // This remounting process resets the internal state and refs of the component, making the autoResize feature work as expected.
    // This is a workaround to simulate the behavior of useRef in functional components using the key prop.
    // Note: Changing the key prop forces React to treat the component as a new instance, which can affect performance,
    // but until its parent is refactored as a class component, this is the only way to make autoResize work.
    useEffect(() => {
        setKey(props.automation || 'resize');
    }, []);

    return (
        <FormContextConsumer {...props}>
            {(properties) => (
                <>
                    <TextArea
                        autoResize={properties.autoResize}
                        className={classnames(
                            'form-control',
                            properties.automation ? 'automation-' + properties.automation : ''
                        )}
                        data-automation-test={properties.automation}
                        disabled={properties.disabled}
                        error={getValidationErrorTranslation(properties.error)}
                        key={key}
                        maxLength={properties.maxLength}
                        name={properties.name}
                        onBlur={async () => {
                            if (properties.onDoValidate) {
                                properties.onDoValidate();
                            }
                        }}
                        onChange={(e) => properties.onChange(e.target.value ? e.target.value : null)}
                        placeholder={properties.placeholder}
                        rows={properties.rows ? properties.rows : 1}
                        sx={properties.style}
                        tabIndex={props.tabIndex}
                        value={properties.value || ''}
                        variant={props.variant}
                    />
                    {properties.reserveErrorSpace && !properties.error && <div style={{ height: '36px' }}></div>}
                </>
            )}
        </FormContextConsumer>
    );
}

export function TextAreaField(props: FormContextProps<ITextAreaInputProps & IFieldProps>) {
    return (
        <Field {...props}>
            <TextAreaInput {...props} />
        </Field>
    );
}
